import { createSlice } from '@reduxjs/toolkit';
import history from '@history';
import _ from '@lodash';
import { setDefaultSettings } from 'app/store/fuse/settingsSlice';
import Request from 'app/base/request';
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from 'app/util/consts';
import { removeCookie, getCookie, fileUrl, resizedUrlFromFileId, urlFromFileId } from 'app/util/utils';
import { hideLoading } from '../../store/fuse/loadingSlice';
import { UserType } from 'app/main/aiaas/util/consts';

export const setUserData = user => async (dispatch, getState) => {
	/*
		You can redirect the logged-in user to a specific route depending on his role
		 */

	history.location.state = {
		redirectUrl: user.redirectUrl // for example 'apps/academy'
	};

	/*
	Set User Settings
	 */
	dispatch(setDefaultSettings(user.data.settings));

	dispatch(setUser(user));
};

export const updateUserSettings = settings => async (dispatch, getState) => {
	const oldUser = getState().auth.user;
	const user = _.merge({}, oldUser, { data: { settings } });

	dispatch(updateUserData(user));

	return dispatch(setUserData(user));
};

export const updateUserShortcuts = shortcuts => async (dispatch, getState) => {
	const { user } = getState().auth;
	const newUser = {
		...user,
		data: {
			...user.data,
			shortcuts
		}
	};

	dispatch(updateUserData(user));

	return dispatch(setUserData(newUser));
};

export const logoutUser = () => (dispatch, getState) => {
	removeCookie(TOKEN_KEY);
	removeCookie(REFRESH_TOKEN_KEY);
	history.push({
		pathname: '/services'
	});
	dispatch(userLoggedOut());
	dispatch(hideLoading({ onBody: true }));
}

export const updateUserData = user => async (dispatch, getState) => {
	if (!user.role || user.role.length === 0) {
		// is guest
		return;
	}
};

const initialState = {
	id: null,
	roles: [], // guest
	isLoggedIn: false,
	data: {
		displayName: '',
		photoURL: 'assets/images/avatars/profile.jpg',
		email: '',
		shortcuts: []
	},
	// userLicense: {
	// 	id: 0,
	// 	licenseId: 0,
	// 	services: [],
	// 	licenseType: 0,
	// 	queries: 0,
	// 	remainingQueries: 0,
	// 	customModel: 0,
	// 	remainingCustomModel: 0,
	// 	hasCustomService: 0,
	// 	diskSpace: 0,
	// 	remainingDiskSpace: 0,
	// 	fileSizeLimit: null,
	// 	aggregationLimit: 0,
	// 	imageDimensionLimit: 0,
	// 	isFree: true,
	// 	isTest: true,
	// 	hasRemainingServices: [],
	// 	remainingServicesAmount: {}
	// }
};

const userSlice = createSlice({
	name: 'auth/user',
	initialState,
	reducers: {
		setUser: (state, action) => action.payload,
		setUserInfo: (state, action) => {
			let userInfo = action.payload;
			return {
				id: userInfo.id,
				roles: userInfo.roles,
				role: userInfo?.roles.length > 0 ? userInfo.roles[0] : UserType.NORMAL,
				isLoggedIn: true,
				data: {
					firstName: userInfo.firstName,
					lastName: userInfo.lastName,
					displayName: userInfo.firstName + ' ' + userInfo.lastName,
					photoURL: userInfo.extraProperties && userInfo.extraProperties.ProfilePhotoFileId ? urlFromFileId(userInfo.extraProperties.ProfilePhotoFileId) : 'assets/images/avatars/profile.jpg',
					email: userInfo.email,
					tel: userInfo.phoneNumber,
					tel2: userInfo.extraProperties?.PhoneNumber2,
					address: userInfo.extraProperties?.Address,
					language: userInfo.extraProperties?.Language,
					photoId: userInfo.extraProperties?.ProfilePhotoFileId,
					shortcuts: []
				},
				extraProperties: userInfo.extraProperties,
				license: userInfo.license?.currentLicense ? { ...userInfo.license?.currentLicense, aggregationLimit: 5 } : null,
				features: userInfo.features,
				renewalLicense: userInfo.license?.renewalLicense,
				licenseSummary: {
					memory: userInfo.license?.currentLicense?.plan?.extraProperties?.Memory,
					initialDiskSpace: userInfo?.features?.find(x => x.feature.code == "storage")?.initialAmount,
					remainedDiskSpace: userInfo?.features?.find(x => x.feature.code == "storage")?.remainedAmount,
					initialGpu: ((userInfo?.features?.find(x => x.feature.code == "gpu")?.initialAmount || 0) / 3600).toFixed(2),
					remainedGpu: ((userInfo?.features?.find(x => x.feature.code == "gpu")?.remainedAmount || 0) / 3600).toFixed(2),
					initialQueries: userInfo?.features?.filter(x => x.feature.code.includes("_service")).map(x => x.initialAmount).reduce((a, b) => a + b, 0),
					remainedQueries: userInfo?.features?.filter(x => x.feature.code.includes("_service")).map(x => x.remainedAmount).reduce((a, b) => a + b, 0),
				},
				services: userInfo?.features?.filter(x => x.feature.code.includes("_service") || x.feature.code == 'gpu').map(x =>
				({
					initialAmount: x.initialAmount,
					remainedAmount: x.remainedAmount,
					code: x.feature.code,
					name: x.feature.code.substring(0, x.feature.code.indexOf("_service")),
					title: x.feature.title,
					id: x.feature.id,
					isActive: x.feature.isActive,
					isExtensible: x.feature.isExtensible,
					isReclaimable: x.feature.isReclaimable,
					isVisible: x.feature.isVisible,
					memory: x.feature.extraProperties?.Memory
				}))
				// userLicense: {
				// 	id: userInfo.userLicense.id,
				// 	licenseId: userInfo.userLicense.licenseId,
				// 	services: userInfo.userLicense.services,
				// 	hasRemainingServices: userInfo.userLicense.hasRemainingServices,
				// 	remainingServicesAmount: userInfo.userLicense.remainingServicesAmount,
				// 	licenseType: userInfo.userLicense.licenseType,
				// 	queries: userInfo.userLicense.queries,
				// 	remainingQueries: userInfo.userLicense.remainingQueries,
				// customModel: userInfo.userLicense.customModel,
				// remainingCustomModel: userInfo.userLicense.remainingCustomModel,
				// 	hasCustomService: userInfo.userLicense.hasCustomService,
				// diskSpace: userInfo.userLicense.diskSpace,
				// remainingDiskSpace: userInfo.userLicense.remainingDiskSpace,
				// 	fileSizeLimit: userInfo.userLicense.fileSizeLimit,
				// 	aggregationLimit: userInfo.userLicense.aggregationLimit,
				// 	imageDimensionLimit: userInfo.userLicense.imageDimensionLimit,
				// 	isFree: userInfo.userLicense.isFree,
				// 	isTest: userInfo.userLicense.isTest
				// }
			};
		},
		updateService: (state, action) => {
			let _services = [...state.services];
			let service = action.payload;
			let index = _services.findIndex(x => x.code == service.code)
			_services[index] = service;
			return ({ ...state, services: _services })
		},
		setUserLicense: (state, action) => ({ ...state, userLicense: action.payload }),
		userLoggedOut: (state, action) => initialState,
		updatePercent: (state, action) => {
			state.uploadedLargeFiles.find(f => f.guid === action.payload.guid).percent = action.payload.percent;
			if (!state.uploadedLargeFiles.find(f => f.percent !== 100)) state.hasUploading = false;
		},
		setUploadedLargeFiles: (state, action) => {
			state.hasUploading = action.payload.hasUploading;
			if (action.payload.uploadLargeFiles) state.uploadedLargeFiles.push(...action.payload.uploadLargeFiles);
		}
	},
	extraReducers: {}
});

export const {
	setUser,
	setUserInfo,
	updateService,
	setUserLicense,
	userLoggedOut,
	updatePercent,
	setUploadedLargeFiles
} = userSlice.actions;

export default userSlice.reducer;
